// Response schemas for the Meta creative-insight endpoints, mirrored from
// bpm-lambdas/meta/metaCreativeAlerting.ts. The inner objects come straight
// from the Meta Graph API and have open-ended shapes, so they are typed as
// Record<string, unknown> / unknown[] rather than `any`.

export interface CreativeDiversityDataItem {
  ad_object_id?: number;
  name?: string;
  spend?: number;
}

export interface CreativeDiversityInsight {
  adset_id: string;
  creative_diversity_label: string;
  creative_diversity_score: string; // numeric string, or "n/a"
  creative_diversity_data: CreativeDiversityDataItem[];
  date_start?: string;
  date_stop?: string;
}

export interface FatigueHistoricalValue {
  timestamp: string; // yyyy-MM-dd
  value: number;
}

// One adset's fatigue time series. `ad_object_id` is the adset id, which is the
// join key to the diversity payload's `adset_id`.
export interface FatigueSummaryEntry {
  ad_object_id: number | string;
  performance_summary: {
    latest_ds?: string;
    metric_value?: number;
    historical_metric_values: FatigueHistoricalValue[];
  };
}

export interface CreativeFatigueInsight {
  creative_fatigued_ads: unknown[];
  creative_fatigue_summary: FatigueSummaryEntry[];
  date_start?: string;
  date_stop?: string;
}

// ---------------------------------------------------------------------------
// Normalized model consumed by the quadrant chart. Both `fatigue` and
// `diversity` are scaled to 0–1 so the quadrant cross can sit at a fixed
// midpoint regardless of the raw score units.
// ---------------------------------------------------------------------------

export interface AssetDataPoint {
  date: string; // ISO date (yyyy-MM-dd)
  fatigue: number; // 0–1
  diversity: number; // 0–1
}

export interface AssetSeries {
  assetId: string; // adset id (joins to diversity adset_id and SCP ad_group_id)
  assetName: string; // representative ad name, else adset id
  campaign: string; // resolved from SCP performance; '' until then / for missing
  diversityLabel: string; // real creative_diversity_label (HIGH/MEDIUM/LOW/n.a.)
  archetype: DiversityArchetypeType; // how mocked diversity moves over time
  points: AssetDataPoint[]; // one entry per date in the global timeline
}

// Diversity has no historical data, so each asset is assigned one of these
// trajectory archetypes (seeded by asset id) to mock how its diversity score
// drifts across the fatigue timeline.
export const DiversityArchetype = {
  STATIC: 'static',
  RISING: 'rising',
  FALLING: 'falling',
  OSCILLATING: 'oscillating'
} as const;
export type DiversityArchetypeType = (typeof DiversityArchetype)[keyof typeof DiversityArchetype];

export const DIVERSITY_ARCHETYPES: DiversityArchetypeType[] = Object.values(DiversityArchetype);

export const ARCHETYPE_LABEL: Record<DiversityArchetypeType, string> = {
  [DiversityArchetype.STATIC]: 'Static',
  [DiversityArchetype.RISING]: 'Rising',
  [DiversityArchetype.FALLING]: 'Falling',
  [DiversityArchetype.OSCILLATING]: 'Oscillating'
};

// Chart vs. tabular view of the same dataset.
export const ViewMode = {
  CHART: 'chart',
  TABLE: 'table'
} as const;
export type ViewModeType = (typeof ViewMode)[keyof typeof ViewMode];

// Top-level page tabs: the Creative Suite dashboard mock vs. the interactive
// quadrant explorer we built.
export const PageTab = {
  SUITE: 'suite',
  EXPLORER: 'explorer'
} as const;
export type PageTabType = (typeof PageTab)[keyof typeof PageTab];

// How chart points are grouped into colors.
export const ColorMode = {
  CAMPAIGN: 'campaign',
  DIVERSITY: 'diversity',
  AD_GROUP: 'adGroup'
} as const;
export type ColorModeType = (typeof ColorMode)[keyof typeof ColorMode];

export const COLOR_MODE_LABEL: Record<ColorModeType, string> = {
  [ColorMode.CAMPAIGN]: 'Campaign',
  [ColorMode.DIVERSITY]: 'Diversity label',
  [ColorMode.AD_GROUP]: 'Ad group'
};

// The cross dividing the four quadrants. Axes are normalized to 0–1, so the
// midpoint is fixed at the center.
export const QUADRANT_MIDPOINT = 0.5;

// Normalized values are kept inside [EDGE_PADDING, 1 - EDGE_PADDING] rather than
// the full [0, 1] so the extreme assets don't sit on the axis edge and get
// clipped by the frame.
export const EDGE_PADDING = 0.06;

// Quadrant labels keyed by (isHighFatigue, isHighDiversity) → label.
export const QuadrantLabel = {
  HIGH_DIVERSITY_LOW_FATIGUE: 'High Diversity · Low Fatigue',
  HIGH_DIVERSITY_HIGH_FATIGUE: 'High Diversity · High Fatigue',
  LOW_DIVERSITY_LOW_FATIGUE: 'Low Diversity · Low Fatigue',
  LOW_DIVERSITY_HIGH_FATIGUE: 'Low Diversity · High Fatigue'
} as const;
export type QuadrantLabelType = (typeof QuadrantLabel)[keyof typeof QuadrantLabel];

// Playback timing / visual tuning.
export const PLAY_STEP_MS = 1000; // wall-clock time spent advancing one date
export const TRAIL_LENGTH = 5; // number of fading "wake" points behind each asset
export const TRAIL_MIN_OPACITY = 0.08;
export const POINT_RADIUS = 6;

// Mock-fallback dataset sizing (used when the real fatigue response can't be
// parsed into a usable time series).
export const MOCK_ASSET_COUNT = 8;
export const MOCK_DATE_COUNT = 30;
export const MOCK_CAMPAIGN_COUNT = 3; // mock assets are spread across this many campaigns
